import React, { useEffect, useMemo, useState } from 'react';
import Map, { Layer, Source } from 'react-map-gl';
import { getAreaCenter, parseAreaGeoJson, serializeAreaGeoJson } from '../utils/areaGeoJson';
import { getMapStyle } from '../utils/mapboxStyle';
import NexradOverlay from './NexradOverlay';
import { normalizeHighlightColor } from '../utils/highlightColor';
import { MAPBOX_TOKEN } from '../utils/mapboxToken';

interface Props {
  value: string;
  highlightColor?: string;
  onChange: (value: string) => void;
}

type DrawingMode = 'polygon' | 'pen';
const PEN_POINT_STEP = 0.06;

function normalizeColor(value?: string | null) {
  return normalizeHighlightColor(value);
}

function shouldAddPenPoint(currentPoints: [number, number][], nextPoint: [number, number]) {
  const previousPoint = currentPoints[currentPoints.length - 1];
  if (!previousPoint) {
    return true;
  }

  const deltaLng = nextPoint[0] - previousPoint[0];
  const deltaLat = nextPoint[1] - previousPoint[1];
  return Math.hypot(deltaLng, deltaLat) >= PEN_POINT_STEP;
}

const DiscussionAreaEditor: React.FC<Props> = ({ value, highlightColor, onChange }) => {
  const parsedState = useMemo(() => {
    try {
      return {
        points: parseAreaGeoJson(value),
        error: null,
      };
    } catch (error: any) {
      return {
        points: [],
        error: error?.message || 'Invalid GeoJSON',
      };
    }
  }, [value]);

  const [draftPoints, setDraftPoints] = useState<[number, number][]>(parsedState.points);
  const [drawingMode, setDrawingMode] = useState<DrawingMode>('polygon');
  const [isPenDrawing, setIsPenDrawing] = useState(false);

  useEffect(() => {
    setDraftPoints(parsedState.points);
    setIsPenDrawing(false);
  }, [parsedState.points]);

  const center = getAreaCenter(draftPoints);
  const activeColor = normalizeColor(highlightColor);

  const polygonCoordinates = useMemo(() => {
    if (draftPoints.length < 3) return null;
    return [...draftPoints, draftPoints[0]];
  }, [draftPoints]);

  const pointFeatures = useMemo(() => ({
    type: 'FeatureCollection' as const,
    features: draftPoints.map(([lng, lat], index) => ({
      type: 'Feature' as const,
      geometry: {
        type: 'Point' as const,
        coordinates: [lng, lat],
      },
      properties: { index },
    })),
  }), [draftPoints]);

  const updatePoints = (nextPoints: [number, number][]) => {
    setDraftPoints(nextPoints);
    onChange(serializeAreaGeoJson(nextPoints));
  };

  const handleMapClick = (event: any) => {
    if (drawingMode === 'pen') {
      return;
    }

    const point: [number, number] = [event.lngLat.lng, event.lngLat.lat];

    updatePoints([...draftPoints, point]);
  };

  const handleMouseDown = (event: any) => {
    if (drawingMode !== 'pen') {
      return;
    }

    const point: [number, number] = [event.lngLat.lng, event.lngLat.lat];
    setIsPenDrawing(true);
    setDraftPoints([point]);
  };

  const handleMouseMove = (event: any) => {
    if (!isPenDrawing || drawingMode !== 'pen') {
      return;
    }

    const point: [number, number] = [event.lngLat.lng, event.lngLat.lat];
    setDraftPoints((currentPoints) => (
      shouldAddPenPoint(currentPoints, point) ? [...currentPoints, point] : currentPoints
    ));
  };

  const handleMouseUp = () => {
    if (!isPenDrawing || drawingMode !== 'pen') {
      return;
    }

    setIsPenDrawing(false);
    if (draftPoints.length < 3) {
      setDraftPoints([]);
      onChange('');
      return;
    }

    onChange(serializeAreaGeoJson(draftPoints));
  };

  const handleUndo = () => {
    if (drawingMode === 'pen') {
      setIsPenDrawing(false);
      if (draftPoints.length > 0) {
        setDraftPoints([]);
        onChange('');
      }
      return;
    }

    updatePoints(draftPoints.slice(0, -1));
  };

  const helperText = drawingMode === 'pen'
      ? isPenDrawing
        ? 'Keep holding and drag to sketch the outline, then release to finish.'
        : 'Hold the mouse button and trace the oval or area directly on the map.'
      : 'Click the map to draw the area of concern. At least 3 points are required.';

  const undoLabel = drawingMode === 'pen'
      ? isPenDrawing
        ? 'Cancel sketch'
        : 'Clear sketch'
      : 'Undo point';

  const canUndo = drawingMode === 'pen'
      ? isPenDrawing || draftPoints.length > 0
      : draftPoints.length > 0;

  return (
    <div className="area-editor-stack">
      <div className="area-editor-toolbar">
        <div className="area-editor-controls">
          <div className="chip-grid compact-chip-grid">
            <button
              type="button"
              className={`chip ${drawingMode === 'polygon' ? 'chip-active' : ''}`}
              onClick={() => {
                setDrawingMode('polygon');
              }}
            >
              Polygon
            </button>
            <button
              type="button"
              className={`chip ${drawingMode === 'pen' ? 'chip-active' : ''}`}
              onClick={() => {
                setDrawingMode('pen');
              }}
            >
              Pen
            </button>
          </div>
          <span className="module-note">{helperText}</span>
        </div>
        <div className="hero-actions">
          <button type="button" className="btn btn-secondary" onClick={handleUndo} disabled={!canUndo}>
            {undoLabel}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              setDraftPoints([]);
              setIsPenDrawing(false);
              onChange('');
            }}
            disabled={draftPoints.length === 0 && !value.trim()}
          >
            Clear area
          </button>
        </div>
      </div>

      <div className="area-map-shell">
        <Map
          key={document.body.dataset.theme === 'light' ? 'light-map' : 'dark-map'}
          initialViewState={{ latitude: center[0], longitude: center[1], zoom: 6 }}
          mapStyle={getMapStyle()}
          mapboxAccessToken={MAPBOX_TOKEN}
          style={{
            width: '100%',
            height: 320,
            cursor: drawingMode === 'pen' ? 'crosshair' : 'grab',
          }}
          dragPan={!isPenDrawing}
          onClick={handleMapClick}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          reuseMaps
        >
          <NexradOverlay />
          {pointFeatures.features.length > 0 && (
            <Source id="editor-points" type="geojson" data={pointFeatures as any}>
              <Layer
                id="editor-points-layer"
                type="circle"
                paint={{
                  'circle-radius': 5,
                  'circle-color': activeColor,
                  'circle-stroke-color': '#ffffff',
                  'circle-stroke-width': 1,
                }}
              />
            </Source>
          )}

          {draftPoints.length >= 2 && (isPenDrawing || draftPoints.length < 3) && (
            <Source
              id="editor-line"
              type="geojson"
              data={{
                type: 'Feature',
                geometry: {
                  type: 'LineString',
                  coordinates: draftPoints,
                },
                properties: {},
              }}
            >
              <Layer
                id="editor-line-layer"
                type="line"
                paint={{
                  'line-color': activeColor,
                  'line-width': 4,
                }}
              />
            </Source>
          )}

          {polygonCoordinates && (
            <Source
              id="editor-polygon"
              type="geojson"
              data={{
                type: 'Feature',
                geometry: {
                  type: 'Polygon',
                  coordinates: [polygonCoordinates],
                },
                properties: {},
              }}
            >
              <Layer
                id="editor-polygon-fill"
                type="fill"
                paint={{
                  'fill-color': activeColor,
                  'fill-opacity': 0.22,
                }}
              />
              <Layer
                id="editor-polygon-line"
                type="line"
                paint={{
                  'line-color': activeColor,
                  'line-width': 2,
                }}
              />
            </Source>
          )}
        </Map>
      </div>

      <div className="form-group">
        <label className="form-label" htmlFor="area-geojson">Area of concern GeoJSON</label>
        <textarea
          id="area-geojson"
          className="form-input form-textarea form-textarea-small"
          value={value}
          onChange={(event) => {
            setIsPenDrawing(false);
            onChange(event.target.value);
          }}
          placeholder='{"type":"Polygon","coordinates":[[[-84.7,42.9],[-84.2,42.9],[-84.2,42.5],[-84.7,42.5],[-84.7,42.9]]]}'
        />
        {parsedState.error ? <p className="field-help">{parsedState.error}</p> : <p className="field-help">You can draw an area on the map with polygon or pen mode, or paste valid Polygon GeoJSON here.</p>}
      </div>
    </div>
  );
};

export default DiscussionAreaEditor;
